import React, { useState, useEffect } from "react";
import {
  Box,
  createStyles,
  Radio,
  Text,
  TextInput,
  Checkbox,
  Button,
  Anchor,
} from "@mantine/core";
import { DateTimePicker } from "@mantine/dates";
import { useMedplum } from "@medplum/react";
import { createQtrResponse } from "./Service";

interface AnswerOption {
  valueString: string;
}

interface QuestionData {
  linkId: string;
  type: "string" | "boolean" | "choice" | "dateTime";
  text: string;
  answerOption?: AnswerOption[];
}

interface Details {
  data: QuestionData[];
  qtrId: any;
  onEnableSubmit?: any;
  onResponseGenerated(e: any): any;
  isSubmitted?:boolean
}

interface AnswerState {
  [key: string]: string | boolean | Date | null;
}

export const ChatQuestion: React.FC<Details> = ({
  data,
  qtrId,
  onEnableSubmit,
  isSubmitted,
  onResponseGenerated,
}) => {
  const [answers, setAnswers] = useState<AnswerState>({});
  // const [isSubmitted, setIsSubmitted] = useState(false);
  const [isSubmitDisabled, setIsSubmitDisabled] = useState(true);
  const medplum = useMedplum();
  const profile = medplum.getProfile();
  const fullName = profile?.name
    ?.map((n: any) => `${n.given.join(" ")} ${n.family}`)
    .join(", ");

  useEffect(() => {
    const allFilled = data.every(
      (item) =>
        answers[item.linkId] !== undefined && answers[item.linkId] !== null
    );
    setIsSubmitDisabled(!allFilled);
    if (allFilled) {
      onEnableSubmit();
      const questionnaireResponse = {
        resourceType: "QuestionnaireResponse",
        status: "completed",
        item: data.map((item) => {
          const answer = answers[item.linkId];
          let answerObj: any = {};

          switch (item.type) {
            case "string":
              answerObj = { valueString: answer };
              break;
            case "boolean":
              answerObj = { valueBoolean: answer };
              break;
            case "dateTime":
              answerObj = { valueDateTime: (answer as Date)?.toISOString() };
              break;
            case "choice":
              answerObj = { valueString: answer };
              break;
            default:
              break;
          }

          return {
            id: `id-${item.linkId}`,
            linkId: item.linkId,
            text: item.text,
            answer: [answerObj],
          };
        }),
        questionnaire: `Questionnaire/${qtrId}`,
        source: {
          reference: `Practitioner/${profile?.id}`,
          display: fullName,
        },
        authored: new Date().toISOString(),
      };
      onResponseGenerated?.(questionnaireResponse);
    }
  }, [answers, data]);

  const handleAnswerChange = (
    linkId: string,
    value: string | boolean | Date | null
  ) => {
    setAnswers((prev) => ({
      ...prev,
      [linkId]: value,
    }));
  };

  const useStyles = createStyles(() => ({
    questionText: {
      fontWeight: 600,
      fontSize: "16px",
      lineHeight: "20px",
    },
    box1: {
      border: "1px solid #EDEDED",
      width: "100%",
      padding: "16px",
      borderRadius: "10px",
      gap: "8px",
      display: "flex",
      flexDirection: "column",
    },
    mainBox: {
      overflowY: "auto",
      padding: "6px",
      maxHeight: "500px",
      scrollBehavior: "smooth",
      "&::-webkit-scrollbar": {
        width: "2px",
      },
      "&::-webkit-scrollbar-thumb": {
        backgroundColor: "#888",
        borderRadius: "10px",
      },
      "&::-webkit-scrollbar-thumb:hover": {
        backgroundColor: "#555",
      },
      "&::-webkit-scrollbar-track": {
        background: "#f1f1f1",
      },
    },
    linkBox: {
      display: "flex",
      flexDirection: "column",
      gap: "8px",
      padding: "16px",
      backgroundColor: "#d6e9f9",
      border: "none",
      borderRadius: "10px",
      marginBottom: "10px",
    },
  }));
  const { classes } = useStyles();

  const handleSubmit = async () => {
    // setIsSubmitted(true);
    const questionnaireResponse = {
      resourceType: "QuestionnaireResponse",
      status: "completed",
      item: data.map((item) => {
        const answer = answers[item.linkId];
        let answerObj: any = {};

        switch (item.type) {
          case "string":
            answerObj = { valueString: answer };
            break;
          case "boolean":
            answerObj = { valueBoolean: answer };
            break;
          case "dateTime":
            answerObj = { valueDateTime: (answer as Date)?.toISOString() };
            break;
          case "choice":
            answerObj = { valueString: answer };
            break;
          default:
            break;
        }

        return {
          id: `id-${item.linkId}`,
          linkId: item.linkId,
          text: item.text,
          answer: [answerObj],
        };
      }),
      questionnaire: `Questionnaire/${qtrId}`,
      source: {
        reference: `Practitioner/${profile?.id}`,
        display: fullName,
      },
      authored: new Date().toISOString(),
    };

    const response = await createQtrResponse(medplum, questionnaireResponse);
  };

  return (
    <Box className={classes.mainBox}>
      <Box className={classes.linkBox}>
        <Text>Access your form here:</Text>
        <Anchor
          onClick={() => {
            window.open(`/admin/${qtrId}`);
          }}
        >
          /forms/{qtrId}
        </Anchor>
      </Box>
      {data.map((item) => (
        <Box key={item.linkId} mb="sm" className={classes.box1}>
          <Text mb="xs" className={classes.questionText}>
            {item?.linkId}: {item.text}
          </Text>
          {item.type === "string" && (
            <TextInput
              placeholder="Type your answer..."
              size="md"
              radius={"sm"}
              onChange={(e) =>
                handleAnswerChange(item.linkId, e.currentTarget.value)
              }
              value={answers[item.linkId] as string}
              disabled={isSubmitted}
            />
          )}
          {item.type === "choice" && item.answerOption && (
            <Radio.Group
              onChange={(value) => handleAnswerChange(item.linkId, value)}
              value={answers[item.linkId] as string}
            >
              <Box sx={{ display: "flex", flexDirection: "row", gap: "16px" }}>
                {item.answerOption.map((option, index) => (
                  <Radio
                    key={index}
                    value={option.valueString}
                    label={option.valueString}
                    disabled={isSubmitted}
                  />
                ))}
              </Box>
            </Radio.Group>
          )}
          {item.type === "boolean" && (
            <Checkbox
              label="Yes/No"
              checked={answers[item.linkId] as boolean}
              onChange={(e) =>
                handleAnswerChange(item.linkId, e.currentTarget.checked)
              }
              disabled={isSubmitted}
            />
          )}
          {item.type === "dateTime" && (
            <Box sx={{ width: "100%" }}>
              <DateTimePicker
                size="md"
                radius={"sm"}
                clearable
                label="Pick date and time"
                placeholder="Pick date and time"
                maw={"100%"}
                mx="auto"
                value={answers[item.linkId] as Date}
                onChange={(value) => handleAnswerChange(item.linkId, value)}
                disabled={isSubmitted}
              />
            </Box>
          )}
        </Box>
      ))}
      <Box sx={{ display: "flex", justifyContent: "flex-end" }}>
        {/* <Button onClick={handleSubmit} disabled={isSubmitDisabled}>
          Submit
        </Button> */}
      </Box>
    </Box>
  );
};
